/**
 * 大六壬 · 中气定位与月将
 *
 * 月将「中气换将」：以太阳过宫为准，取占时**之前最近的一个中气**对应月将。
 * lunar-javascript 的 getJieQiTable() 覆盖某一农历年前后约 15 个月，边界项键名为拼音别名
 * （DA_XUE / DONG_ZHI / XIAO_HAN / DA_HAN / LI_CHUN / YU_SHUI / JING_ZHE），此处统一归一化；
 * 并汇总前后三个农历年的节气表以保证任意日期都能定位到上一个中气。
 */
import { Lunar } from 'lunar-javascript';
import { ZHONG_QI, yueJiangByQi } from './constants';
/** 节气表拼音别名 → 中文名 */
const QI_ALIAS = {
    DA_XUE: '大雪',
    DONG_ZHI: '冬至',
    XIAO_HAN: '小寒',
    DA_HAN: '大寒',
    LI_CHUN: '立春',
    YU_SHUI: '雨水',
    JING_ZHE: '惊蛰',
    CHUN_FEN: '春分',
    QING_MING: '清明',
    GU_YU: '谷雨',
    LI_XIA: '立夏',
    XIAO_MAN: '小满',
    MANG_ZHONG: '芒种',
    XIA_ZHI: '夏至',
    XIAO_SHU: '小暑',
    DA_SHU: '大暑',
    LI_QIU: '立秋',
    CHU_SHU: '处暑',
    BAI_LU: '白露',
    QIU_FEN: '秋分',
    HAN_LU: '寒露',
    SHUANG_JIANG: '霜降',
    LI_DONG: '立冬',
    XIAO_XUE: '小雪',
};
const ZHONG_QI_SET = new Set(ZHONG_QI);
/** Solar → JS Date（按本地时区） */
function solarToDate(s) {
    return new Date(s.getYear(), s.getMonth() - 1, s.getDay(), s.getHour(), s.getMinute(), s.getSecond());
}
/** 归一化节气键名 */
function normalizeQi(key) {
    return QI_ALIAS[key] ?? key;
}
/**
 * 汇总 date 前后三个农历年的全部中气（按时间升序，去重）。
 */
function collectZhongQi(date) {
    const map = new Map();
    for (let y = date.getFullYear() - 1; y <= date.getFullYear() + 1; y += 1) {
        // 取该公历年 6 月中（必落在对应农历年内）以获取该农历年的节气表
        const probe = new Date(y, 5, 15, 12, 0, 0);
        const table = Lunar.fromDate(probe).getJieQiTable();
        for (const key of Object.keys(table)) {
            const qi = normalizeQi(key);
            if (!ZHONG_QI_SET.has(qi))
                continue;
            const solar = table[key];
            if (!solar)
                continue;
            const time = solarToDate(solar);
            map.set(time.getTime(), { qi, time });
        }
    }
    return [...map.values()].sort((a, b) => a.time.getTime() - b.time.getTime());
}
/**
 * 定位占时之前最近的中气（月将换将依据）。
 * @param date 占卜时刻
 */
export function currentZhongQi(date) {
    const list = collectZhongQi(date);
    const t = date.getTime();
    let hit = null;
    for (const item of list) {
        if (item.time.getTime() <= t)
            hit = item;
        else
            break;
    }
    if (!hit) {
        // 理论上不可达（表已覆盖前后三年）；兜底取最早一个
        const first = list[0];
        if (!first)
            throw new Error('无法定位中气：节气表为空');
        return first;
    }
    return hit;
}
/** 取当前月将（含换将中气与交节时刻） */
export function currentYueJiang(date) {
    const zq = currentZhongQi(date);
    const yj = yueJiangByQi(zq.qi);
    if (!yj)
        throw new Error(`中气「${zq.qi}」无对应月将`);
    return { yueJiang: yj, qi: zq.qi, qiTime: zq.time };
}
//# sourceMappingURL=jieQi.js.map